
import { Component, Mixins, Ref, Watch } from "vue-property-decorator";
import mixin from "./mixin";
import { getModule } from "vuex-module-decorators";
import { OrderModule, SystemModule } from "@/store/modules/";
import * as modals from "@/lib/modals";
import { OrderResource } from "@/models/api/orders/get";
import Address from "@/components/widgets/Address.vue";
import permissions from "@/security/permissions";
import { formatDateWithTime, formatBool, formatPhoneNumber } from "@/utils";
import _ from "lodash";

const systemModule = getModule(SystemModule);
const orderModule = getModule(OrderModule);

@Component({ name: modals.orderPreviewModal, components: { Address } })
export default class OrderPreviewModal extends Mixins(mixin) {
  viewAsJson: boolean = false;
  loading: boolean = true;
  availableItems: Array<any> = null;
  showJson: boolean = true;

  @Ref() readonly noteBody!: HTMLInputElement;

  get modalData() {
    return systemModule.modalData;
  }

  get currentOrderPreview() {
    return JSON.stringify(orderModule.currentOrderPreview, null, 2);
  }

  get commodities() {
    return orderModule.currentOrderPreviewCommodities;
  }

  get orderItems() {
    return orderModule.currentOrderPreviewOrderItems;
  }

  get oP() {
    return orderModule.currentOrderPreview;
  }

  get consigneeContacts() {
    return orderModule.currentOrderConsigneeContacts;
  }

  created() {
    this.loading = true;
  }

  @Watch("generalItems")
  itemsChanged(val: any) {
    if (val) {
      this.availableItems = val.filter(x => x.permission);
      this.showJson = val.filter(x => !x.permission).length === 0;
    }
  }

  get generalItems() {
    if (this.oP) {
      return [
        { name: "System #", value: this.oP.number, permission: true },
        {
          name: "CRN #",
          value: this.oP.crn,
          permission: permissions.order.view.crn ? true : false
        },
        { name: "Has POD", value: this.oP.hasPOD ? "Yes" : "No", permission: true },
        { name: "Has Barcode Order", value: this.oP.barcodeOrderId ? "Yes" : "No", permission: true },
        { name: "Unit Count", value: this.oP.unitCount, permission: true },
        { name: "Cubes", value: this.oP.cubes, permission: true },
        { name: "Ship Date", value: formatDateWithTime(this.oP.shipDate), permission: true },
        {
          name: "Anticipated Receive Date",
          value: formatDateWithTime(this.oP.anticipatedReceiveDate),
          permission: permissions.order.view.dates ? true : false
        },
        {
          name: "Actual Receive Date",
          value: formatDateWithTime(this.oP.actualReceiveDate),
          permission: permissions.order.view.dates ? true : false
        },
        {
          name: "Requested Delivery Date",
          value: formatDateWithTime(this.oP.requestedDeliveryDate),
          permission: permissions.order.view.dates ? true : false
        },
        {
          name: "Scheduled Delivery Date",
          value: formatDateWithTime(this.oP.scheduledDeliveryDate),
          permission: permissions.order.view.dates ? true : false
        },
        {
          name: "Actual Delivery Date",
          value: formatDateWithTime(this.oP.actualDeliveryDate),
          permission: permissions.order.view.dates ? true : false
        },
        { name: "Date Placed", value: formatDateWithTime(this.oP.datePlaced), permission: true },
        {
          name: "Time Frame Start",
          value: formatDateWithTime(this.oP.timeframeStart),
          permission: true
        },
        {
          name: "Time Frame End",
          value: formatDateWithTime(this.oP.timeframeEnd),
          permission: true
        },
        {
          name: "Follow Up Date",
          value: formatDateWithTime(this.oP.followUpDate),
          permission: true
        },
        {
          name: "Dock Search Date",
          value: formatDateWithTime(this.oP.dockSearchDate),
          permission: true
        },
        {
          name: "Facility",
          value: this.oP.facility,
          permission: permissions.order.view.facility ? true : false
        },
        { name: "Remarks", value: this.oP.remarks, permission: true },
        { name: "Special Requests", value: this.oP.specialRequests, permission: true },
        {
          name: "Manufacturer",
          value: this.oP.agentManufacturer ? this.oP.agentManufacturer : this.oP.manufacturer,
          permission: permissions.order.view.manufacturer ? true : false
        },
        {
          name: "Delivery Type",
          value: this.getDeliveryType(this.oP.deliveryType),
          permission: permissions.order.view.deliveryType ? true : false
        },
        { name: "Status", value: this.oP.status, permission: true },
        { name: "Miles", value: this.oP.miles, permission: true },
        { name: "Booked by", value: this.oP.bookedBy, permission: true },
        {
          name: "Carrier",
          value: this.oP.carrier,
          permission: permissions.order.view.carrier ? true : false
        },
        { name: "Received", value: formatBool(this.oP.received), permission: true },
        { name: "Intermediate Consignee", value: this.oP.intermediateConsignee, permission: true },
        {
          name: "COD Pending",
          value: formatBool(this.oP.codPending),
          permission: permissions.order.view.codPending ? true : false
        },
        {
          name: "Customer Not Ready",
          value: formatBool(this.oP.customerNotReady),
          permission: true
        },
        { name: "Order Type", value: this.oP.orderType, permission: true },
        { name: "Driver Note", value: this.oP.driverNote, permission: true },
        { name: "Rate By", value: this.oP.rateBy, permission: true },
        { name: "Load Note", value: this.oP.loadDate, permission: true },
        { name: "Has Notes", value: formatBool(this.oP.hasNotes), permission: true },
        {
          name: "Has Ships with Orders",
          value: formatBool(this.oP.hasShipsWithOrders),
          permission: true
        },
        { name: "Contact Company Id", value: this.oP.contactCompanyId, permission: true },
        { name: "Is Scheduled", value: formatBool(this.oP.isScheduled), permission: true },
        { name: "Wants ASAP", value: formatBool(this.oP.isAsapDelivery), permission: true }
      ];
    }
  }

  getDeliveryType(value) {
    if(orderModule.currentOrderPreview.deliveryForId > 0){
      const foundValue = _.find(systemModule.allAgentDeliveryTypes, {"typeCode": orderModule.currentOrderPreview.deliveryType})
      if(!foundValue)
        return value;
      return foundValue.name;
    } else {
      return value;
    }
  }

  async getOrderDetails(orderNum) {
    await orderModule.getOrderPreview(orderNum);
    this.loading = false;
  }

  getFormattedPhoneNumber(phoneNumber) {
    return phoneNumber ? formatPhoneNumber(phoneNumber) : "";
  }

  getPhoneTypeIcon(phoneType) {
    switch (phoneType) {
      case "M":
        return "mobile";
      case "H":
        return "home";
      case "B":
        return "phone";
      default:
        return "home";
    }
  }

  getPhoneTypeText(phoneType) {
    switch (phoneType) {
      case "M":
        return "Mobile";
      case "H":
        return "Home";
      case "B":
        return "Business";
      default:
        return "Phone";
    }
  }

  async onShow() {
    this.loading = true;
    orderModule.setCurrentOrderPreview({});
    await this.orderModule.loadOrder(this.modalData);
    this.getOrderDetails(this.modalData.toString());
  }
}
